import PageShell from "@/components/PageShell";
import PageHero from "@/components/PageHero";
import { Link } from "react-router-dom";
import { ArrowRight, Code2, Terminal, Cloud, HardDrive, Brain, Globe2 } from "lucide-react";

const TOOLS = [
    { icon: Code2, k: "SDKs", body: "TypeScript, Python, Go, Rust." },
    { icon: Terminal, k: "CLI", body: "Single-binary deploy and manage." },
    { icon: Cloud, k: "REST API", body: "Idiomatic HTTP for every primitive." },
    { icon: Cloud, k: "gRPC API", body: "High-throughput, streaming-friendly." },
    { icon: Code2, k: "Solidity Contracts", body: "EVM-native precompiles." },
    { icon: Globe2, k: "Explorer", body: "Inspect blocks, txs, proofs, jobs." },
];

const STEPS = [
    "Connect wallet",
    "Deploy smart contract",
    "Upload container",
    "Choose node region",
    "Attach storage",
    "Register DNS",
    "Set revenue share",
    "Monitor usage",
];

const CODE = `dcx deploy ./app \\
  --compute standard \\
  --region auto \\
  --storage encrypted \\
  --domain myapp.dcx`;

export default function DevelopersPage() {
    return (
        <PageShell>
            <PageHero
                eyebrow="Developers"
                title="Build Cloud-Native dApps"
                accent="on DeCloudX."
                sub="Deploy applications, smart contracts, AI models, storage workflows, and DNS records using DeCloudX SDKs, APIs, CLI, and EVM tooling."
            />

            {/* Developer Console mockup */}
            <section className="relative py-16 md:py-24 border-t border-line">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="surface-strong rounded-2xl overflow-hidden" data-testid="developer-console">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-line bg-card-muted">
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-1.5">
                                    <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
                                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/80" />
                                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
                                </div>
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-ink">DeCloudX Developer Console · myapp.dcx</span>
                            </div>
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink">v1.0</span>
                        </div>
                        <div className="grid lg:grid-cols-12 gap-0">
                            <div className="lg:col-span-4 p-6 border-b lg:border-b-0 lg:border-r border-line">
                                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink mb-3">Deployment</div>
                                <div className="space-y-2 text-sm">
                                    {[
                                        { i: Cloud, k: "Compute", v: "Standard · auto-region" },
                                        { i: HardDrive, k: "Storage", v: "Encrypted · 100 GiB" },
                                        { i: Brain, k: "AI Model", v: "llama-3.1-70b" },
                                        { i: Globe2, k: "Domain", v: "myapp.dcx" },
                                    ].map((it, k) => (
                                        <div key={k} className="flex items-center gap-3 p-2.5 rounded-lg surface">
                                            <it.i size={14} className="text-brand-blue" strokeWidth={1.5} />
                                            <div className="flex-1 min-w-0">
                                                <div className="font-mono text-[10px] uppercase tracking-wider text-faint-ink">{it.k}</div>
                                                <div className="text-xs text-ink truncate">{it.v}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <button className="btn-primary justify-center w-full mt-4">Deploy</button>
                            </div>
                            <div id="cli" className="lg:col-span-8 p-6 bg-card-muted/40">
                                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink mb-3">Terminal</div>
                                <pre className="surface rounded-xl p-5 overflow-x-auto text-xs leading-relaxed text-ink font-mono">
<span className="text-brand-violet">$</span> {CODE}
{"\n"}
<span className="text-faint-ink">→ Validating contract……  </span><span className="text-emerald-600 dark:text-emerald-400">ok</span>
{"\n"}<span className="text-faint-ink">→ Uploading container…  </span><span className="text-emerald-600 dark:text-emerald-400">ok</span>
{"\n"}<span className="text-faint-ink">→ Scheduling nodes…    </span><span className="text-emerald-600 dark:text-emerald-400">3 selected · EU-WEST</span>
{"\n"}<span className="text-faint-ink">→ Attaching storage…   </span><span className="text-emerald-600 dark:text-emerald-400">16/10 RS · 100 GiB</span>
{"\n"}<span className="text-faint-ink">→ Registering DNS…     </span><span className="text-emerald-600 dark:text-emerald-400">myapp.dcx</span>
{"\n"}<span className="text-faint-ink">→ Deploying contract…  </span><span className="text-emerald-600 dark:text-emerald-400">0x84…D2a1</span>
{"\n"}
<span className="text-brand-cyan">✓ Live</span> · https://myapp.dcx
                                </pre>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Tools */}
            <section id="sdks" className="relative py-16 md:py-24 border-t border-line">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="overline mb-3">Tooling</div>
                    <h2 className="font-heading font-black text-2xl sm:text-3xl text-ink max-w-3xl">Everything you need to build cloud-native dApps.</h2>
                    <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        {TOOLS.map((t, i) => (
                            <div key={i} className="surface card-lift p-5 rounded-xl" data-testid={`tool-${i}`}>
                                <t.icon size={17} className="text-brand-violet mb-3" strokeWidth={1.5} />
                                <div className="font-heading font-bold text-sm text-ink">{t.k}</div>
                                <div className="text-xs text-muted-ink mt-1.5 leading-relaxed">{t.body}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Deployment flow */}
            <section id="apis" className="relative py-16 md:py-24 border-t border-line">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="overline mb-3">Deployment Flow</div>
                    <h2 className="font-heading font-black text-2xl sm:text-3xl text-ink max-w-3xl">Eight steps to a live cloud-native dApp.</h2>
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {STEPS.map((s, i) => (
                            <div key={i} className="surface p-5 rounded-xl flex items-start gap-3" data-testid={`step-${i}`}>
                                <div className="w-8 h-8 rounded-lg brand-gradient-bg text-white flex items-center justify-center font-mono text-xs font-bold shrink-0">{String(i+1).padStart(2,"0")}</div>
                                <div className="text-sm text-ink mt-1">{s}</div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-12">
                        <Link to="/contact?role=developer" className="btn-primary">Start Building <ArrowRight size={15} strokeWidth={2} /></Link>
                    </div>
                </div>
            </section>
        </PageShell>
    );
}
